import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useReference } from "@/lib/useReference";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import { LetterPolicyCard } from "@/components/LetterPolicyCard";

export default function Achievements() {
  const navigate = useNavigate();
  const { ref } = useReference();
  // Team types are derived from the teams in the database (code -> name), so every
  // current or future team type (Dance, Cheer, Pep Squad, Theater, …) is available
  // automatically without a code change.
  const TYPES = useMemo(() => {
    const map = new Map();
    (ref.teams || []).forEach((t) => { if (t.code && !map.has(t.code)) map.set(t.code, t.name || t.code); });
    return [...map.entries()].map(([v, l]) => ({ v, l })).sort((a, b) => a.l.localeCompare(b.l));
  }, [ref.teams]);
  const [type, setType] = useState("");
  useEffect(() => { if (!type && TYPES.length) setType(TYPES[0].v); }, [TYPES, type]);
  const [items, setItems] = useState([]);
  const [fDistrict, setFDistrict] = useState("all");
  const [fCampus, setFCampus] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ scope: "district", district_id: "", campus_id: "", team_id: "", label: "", points: 5, is_active: true });
  // Lettering-award policy team selector (admin edits a specific team's policy)
  const [pDistrict, setPDistrict] = useState("");
  const [pCampus, setPCampus] = useState("");
  const [pTeam, setPTeam] = useState("");
  const pCampusOpts = ref.campuses.filter((c) => c.district_id === pDistrict);
  const pTeamOpts = ref.teams.filter((t) => t.campus_id === pCampus && t.code === type);

  const load = useCallback(async () => { if (!type) return; const { data } = await api.get(`/admin/achievement-options?team_type=${type}`); setItems(data.items); }, [type]);
  useEffect(() => { load(); }, [load]);

  const teamById = (id) => ref.teams.find((t) => t.id === id);
  const shown = items.filter((o) => {
    if (fDistrict !== "all" && o.district_id !== fDistrict) return false;
    if (fCampus !== "all") {
      if (!o.team_id) return true; // district-wide applies to all campuses
      const t = teamById(o.team_id);
      return t && t.campus_id === fCampus;
    }
    return true;
  });

  const filterCampusOpts = ref.campuses.filter((c) => fDistrict === "all" || c.district_id === fDistrict);
  const formCampuses = ref.campuses.filter((c) => c.district_id === form.district_id);
  const formTeams = ref.teams.filter((t) => t.campus_id === form.campus_id && t.code === type);

  const openNew = () => { setForm({ scope: "district", district_id: ref.districts[0]?.id || "", campus_id: "", team_id: "", label: "", points: 5, is_active: true }); setOpen(true); };
  const save = async () => {
    if (!form.district_id || !form.label) { toast.error("District and label are required"); return; }
    if (form.scope === "team" && !form.team_id) { toast.error("Select a team"); return; }
    const payload = { team_type: type, district_id: form.district_id, label: form.label, points: parseInt(form.points, 10), is_active: form.is_active, team_id: form.scope === "team" ? form.team_id : null };
    try { await api.post("/admin/achievement-options", payload); toast.success("Created"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggle = async (o) => { try { await api.put(`/admin/achievement-options/${o.id}`, { is_active: !o.is_active }); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const updatePoints = async (o, points) => { try { await api.put(`/admin/achievement-options/${o.id}`, { points: parseInt(points, 10) }); toast.success("Updated"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const remove = async (o) => { try { await api.delete(`/admin/achievement-options/${o.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const scopeLabel = (o) => o.team_id ? (teamById(o.team_id)?.name || "Team") : "District-wide";

  return (
    <div>
      <button onClick={() => navigate("/admin/lettering")} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800" data-testid="back-to-lettering"><ArrowLeft className="h-4 w-4" /> Back to Letter Points Review</button>
      <PageHeader title="Achievement Options" subtitle="District-wide options apply to every team of that type. Team options apply to one team only." />
      <div className="mb-4 flex items-center gap-2">
        <Label className="text-sm font-semibold text-slate-700">Program</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-56" data-testid="achievement-type-select"><SelectValue placeholder="Select program" /></SelectTrigger>
          <SelectContent>
            {TYPES.map((t) => <SelectItem key={t.v} value={t.v} data-testid={`achievement-type-${t.v}`}>{t.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mb-4 rounded-xl border border-violet-200 bg-white p-4 shadow-sm" data-testid="admin-letter-policy-block">
        <p className="mb-2 text-sm font-semibold text-slate-700">Lettering Award Policy — pick a team to configure</p>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Select value={pDistrict} onValueChange={(v) => { setPDistrict(v); setPCampus(""); setPTeam(""); }}>
            <SelectTrigger className="w-48" data-testid="policy-district-select"><SelectValue placeholder="District" /></SelectTrigger>
            <SelectContent>{ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={pCampus} onValueChange={(v) => { setPCampus(v); setPTeam(""); }} disabled={!pDistrict}>
            <SelectTrigger className="w-48" data-testid="policy-campus-select"><SelectValue placeholder="Campus" /></SelectTrigger>
            <SelectContent>{pCampusOpts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
          </Select>
          <Select value={pTeam} onValueChange={setPTeam} disabled={!pCampus}>
            <SelectTrigger className="w-56" data-testid="policy-team-select"><SelectValue placeholder={pCampus && !pTeamOpts.length ? `No ${TYPES.find((t) => t.v === type)?.l || ""} team here — change Program` : `${TYPES.find((t) => t.v === type)?.l || ""} team`} /></SelectTrigger>
            <SelectContent>{pTeamOpts.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {pTeam
          ? <LetterPolicyCard key={pTeam} getUrl={`/admin/teams/${pTeam}/letter-policy`} putUrl={`/admin/teams/${pTeam}/letter-policy`} editable />
          : <p className="text-xs text-slate-400">Select a district, campus and team to view or edit its lettering award policy.</p>}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select value={fDistrict} onValueChange={(v) => { setFDistrict(v); setFCampus("all"); }}>
          <SelectTrigger className="w-48" data-testid="achievement-district-filter"><SelectValue placeholder="District" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All districts</SelectItem>
            {ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCampus} onValueChange={setFCampus}>
          <SelectTrigger className="w-48" data-testid="achievement-campus-filter"><SelectValue placeholder="Campus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All campuses</SelectItem>
            {filterCampusOpts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={openNew} className="ml-auto rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-achievement-btn"><Plus className="mr-2 h-4 w-4" /> Add option</Button>
      </div>
      {shown.length === 0 ? <EmptyState title="No options" description="Add achievement options for this team type." testId="achievements-empty" /> : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table data-testid="achievements-table">
            <TableHeader><TableRow className="bg-slate-50">{["Achievement", "Points", "Scope", "Active", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {shown.map((o) => (
                <TableRow key={o.id} data-testid={`achievement-row-${o.id}`}>
                  <TableCell className="font-medium text-slate-800">{o.label}</TableCell>
                  <TableCell>
                    <Input type="number" defaultValue={o.points} className="h-8 w-20 tabular-nums" onBlur={(e) => Number(e.target.value) !== o.points && updatePoints(o, e.target.value)} data-testid={`achievement-points-${o.id}`} />
                  </TableCell>
                  <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${o.team_id ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"}`}>{scopeLabel(o)}{o.created_by === "coach" ? " · coach" : ""}</span></TableCell>
                  <TableCell><Switch checked={o.is_active} onCheckedChange={() => toggle(o)} data-testid={`achievement-toggle-${o.id}`} /></TableCell>
                  <TableCell className="text-right"><Button size="icon" variant="ghost" className="text-red-500" onClick={() => remove(o)} data-testid={`achievement-delete-${o.id}`}><Trash2 className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="achievement-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add achievement option ({TYPES.find((t) => t.v === type)?.l})</DialogTitle><DialogDescription>District-wide applies to all {TYPES.find((t) => t.v === type)?.l} teams in the district; team scope applies to one team.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Scope</Label>
              <Select value={form.scope} onValueChange={(v) => setForm((f) => ({ ...f, scope: v, campus_id: "", team_id: "" }))}>
                <SelectTrigger data-testid="achievement-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="district">District-wide (all teams of this type)</SelectItem>
                  <SelectItem value="team">Specific team</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>District</Label>
              <Select value={form.district_id} onValueChange={(v) => setForm((f) => ({ ...f, district_id: v, campus_id: "", team_id: "" }))}>
                <SelectTrigger data-testid="achievement-district-select"><SelectValue placeholder="District" /></SelectTrigger>
                <SelectContent>{ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.scope === "team" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Campus</Label>
                  <Select value={form.campus_id} onValueChange={(v) => setForm((f) => ({ ...f, campus_id: v, team_id: "" }))} disabled={!form.district_id}>
                    <SelectTrigger data-testid="achievement-campus-select"><SelectValue placeholder="Campus" /></SelectTrigger>
                    <SelectContent>{formCampuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Team</Label>
                  <Select value={form.team_id} onValueChange={(v) => setForm((f) => ({ ...f, team_id: v }))} disabled={!form.campus_id}>
                    <SelectTrigger data-testid="achievement-team-select"><SelectValue placeholder="Team" /></SelectTrigger>
                    <SelectContent>{formTeams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="space-y-2"><Label>Label</Label><Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} data-testid="achievement-label-input" /></div>
            <div className="space-y-2"><Label>Points</Label><Input type="number" value={form.points} onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))} data-testid="achievement-points-input" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="achievement-active-switch" /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="achievement-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
